import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { RosterGrid } from '@/components/RosterGrid/RosterGrid';
import { PopulationStats } from '@/components/Roster/PopulationStats';
import { ChangesetPanel } from '@/components/Roster/ChangesetPanel';
import { KeepApartSection } from '@/components/Roster/KeepApartSection';
import { RosterUploadButton, LoadedRoster } from '@/components/Roster/RosterUploadButton';
import { RosterImportDialog } from '@/components/Roster/RosterImportDialog';
import { DraftPatch, RosterParticipant } from '@/types/roster';
import {
  upsertParticipant, deleteParticipant as apiDeleteParticipant,
  generateFromRoster, discardRosterChanges,
  addKeepApart, removeKeepApart,
  uploadRoster, updateRosterDraft, deleteRosterDraft, RosterUpload, saveRosterOrder,
} from '@/api/roster';
import {
  useRoster, useAssignmentSetMetadata, useCanonicalRoster, useKeepApart, useRosterDrafts,
} from '@/hooks/queries';
import { computeChangeset, CanonicalParticipant } from '@/utils/rosterDiff';
import { useProgram } from '@/contexts/ProgramContext';
import { useQueryClient } from '@tanstack/react-query';
import { MAX_TABLES, MAX_SESSIONS } from '@/constants';
import { AlertCircle, HelpCircle, Loader2, Pencil } from 'lucide-react';
import { movePartnerAdjacent } from '@/utils/sortWithPartnerAdjacency';
import { RosterSort, RosterSortColumn, sortRoster } from '@/utils/rosterSort';
import { generateTestParticipants } from '@/utils/seedTestRoster';

type SaveStatus = 'saved' | 'saving' | 'error';

interface AssignmentSetSummary {
  assignment_set_id: string;
  num_tables: number;
  num_sessions: number;
}

interface CanonicalRoster {
  participants: CanonicalParticipant[];
  num_tables: number | null;
  num_sessions: number | null;
}

/**
 * The draft roster in the shape the assignment set froze. The canonical copy
 * stores a partner *name*, not an id, so a partnered person would read as
 * changed on every comparison unless the draft is translated first.
 *
 * Keep-apart rules get the same treatment, mirroring the server's
 * `_roster_to_participant_list` exactly: each pair stamps each person's name
 * on the other, sorted and deduped, and a pair naming an id that no longer
 * resolves is dropped — just as a dangling partner_id resolves to null. The
 * stamp is keyed by name, so two people sharing a name both receive a rule
 * aimed at either of them, which is the safe direction and what the server
 * does. Any divergence here would have the page report changes that aren't
 * real, or miss ones that are.
 */
function toCanonical(
  participants: RosterParticipant[],
  keepApartPairs: [string, string][],
): CanonicalParticipant[] {
  const nameById = new Map(participants.map(p => [p.id, p.name]));

  const keepApartNames = new Map<string, string[]>();
  participants.forEach(p => keepApartNames.set(p.name, []));
  keepApartPairs.forEach(([aId, bId]) => {
    const aName = nameById.get(aId);
    const bName = nameById.get(bId);
    if (aName === undefined || bName === undefined || aName === bName) return;
    keepApartNames.get(aName)!.push(bName);
    keepApartNames.get(bName)!.push(aName);
  });

  return participants.map(p => ({
    name: p.name,
    religion: p.religion,
    gender: p.gender,
    partner: p.partner_id ? nameById.get(p.partner_id) ?? null : null,
    is_facilitator: p.is_facilitator ?? false,
    keep_together: p.keep_together ?? false,
    keep_apart: Array.from(new Set(keepApartNames.get(p.name) ?? [])).sort(),
  }));
}

export function RosterPage() {
  const navigate = useNavigate();
  const { currentProgram } = useProgram();
  const queryClient = useQueryClient();
  const { data: rosterData, isLoading: rosterLoading, error: fetchError } = useRoster();
  const { data: metadata, isLoading: metadataLoading } = useAssignmentSetMetadata(
    currentProgram?.id ?? null,
  );
  const currentSet: AssignmentSetSummary | null =
    (metadata as AssignmentSetSummary | undefined) ?? null;
  const {
    data: canonicalData,
    isLoading: canonicalLoading,
    error: canonicalError,
  } = useCanonicalRoster(currentProgram?.id ?? null);
  const canonical = (canonicalData as CanonicalRoster | undefined) ?? null;
  const {
    data: keepApartData,
    isLoading: keepApartLoading,
    error: keepApartError,
  } = useKeepApart(currentProgram?.id ?? null);
  const keepApartPairs = keepApartData ?? [];
  const {
    data: draftData,
    isLoading: draftsLoading,
    error: draftsError,
  } = useRosterDrafts(currentProgram?.id ?? null);
  const drafts = draftData?.drafts ?? [];

  // Every one of the three, not just the roster. The lock is derived from all
  // of them, so rendering before they land shows an established program as a
  // brand-new one: unlocked, offering "Generate assignments", with an editable
  // grid that autosaves each keystroke. A guard that is off for the first paint
  // is not a guard.
  // Keep-apart belongs here for the same reason: the lock is derived from it
  // too, so painting before it lands shows a locked program as dirty - grid
  // editable, autosaving, Discard on screen - and then silently flips back.
  // Drafts feed the lock too, so painting before they land could lock a page
  // that has drafts to fix.
  const loading =
    rosterLoading || metadataLoading || canonicalLoading || keepApartLoading || draftsLoading;

  const [participants, setParticipants] = useState<RosterParticipant[]>([]);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const [error, setError] = useState<string | null>(null);
  const [numTables, setNumTables] = useState('4');
  const [numSessions, setNumSessions] = useState('5');
  const [generating, setGenerating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Deliberately not persisted: "I pressed Edit but changed nothing" is not
  // worth remembering, and a reload should put the guard back.
  const [armed, setArmed] = useState(false);
  const [upload, setUpload] = useState<LoadedRoster | null>(null);
  // Only for the header arrow. Rows are sorted once per click, never kept sorted.
  const [sort, setSort] = useState<RosterSort | null>(null);

  // "Add test data" renders into the top nav bar (next to Admin) so it's out
  // of the way of roster screenshots. The slot div is in NavBar (App.tsx) and
  // doesn't exist in the DOM on RosterPage's first render pass, so grab it
  // after mount.
  const [navActionsSlot, setNavActionsSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setNavActionsSlot(document.getElementById('nav-actions-slot'));
  }, []);

  // The selects have to start on the plan's real shape, or a program built as
  // anything other than the 4x5 default would read as changed forever.
  useEffect(() => {
    if (!currentSet) return;
    setNumTables(String(currentSet.num_tables));
    setNumSessions(String(currentSet.num_sessions));
  }, [currentSet?.assignment_set_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (rosterData) {
      // The server's order is the order shown. Nothing reorders on load.
      setParticipants(rosterData);
    }
  }, [rosterData]);

  const handleUpdate = useCallback(async (id: string, data: Omit<RosterParticipant, 'id'>) => {
    setSaveStatus('saving');
    setParticipants(prev => prev.map(p => p.id === id ? { ...p, ...data } : p));

    const oldParticipant = participants.find(p => p.id === id);
    const oldPartnerId = oldParticipant?.partner_id;
    const newPartnerId = data.partner_id;
    // Picking a partner pulls them under this row. That's the one order change
    // no header click made, so it's saved here.
    const pulledUp = newPartnerId && newPartnerId !== oldPartnerId
      ? movePartnerAdjacent(
          participants.map(p => (p.id === id ? { ...p, partner_id: newPartnerId } : p)),
          id,
        )
      : null;

    try {
      await upsertParticipant(currentProgram!.id, id, data);

      if (oldPartnerId !== newPartnerId) {
        if (oldPartnerId) {
          const oldPartner = participants.find(p => p.id === oldPartnerId);
          if (oldPartner && oldPartner.partner_id === id) {
            await upsertParticipant(currentProgram!.id, oldPartnerId, { ...oldPartner, partner_id: null, keep_together: false });
            setParticipants(prev => prev.map(p =>
              p.id === oldPartnerId ? { ...p, partner_id: null, keep_together: false } : p
            ));
          }
        }
        if (newPartnerId) {
          const newPartner = participants.find(p => p.id === newPartnerId);
          if (newPartner) {
            await upsertParticipant(currentProgram!.id, newPartnerId, { ...newPartner, partner_id: id });
            setParticipants(prev => {
              const updated = prev.map(p =>
                p.id === newPartnerId ? { ...p, partner_id: id } : p
              );
              return movePartnerAdjacent(updated, id);
            });
            if (pulledUp && pulledUp.some((p, i) => p.id !== participants[i]?.id)) {
              await saveRosterOrder(currentProgram!.id, pulledUp.map(p => p.id));
            }
          }
        }
      }

      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
    }
  }, [participants, currentProgram]);

  const handleSort = useCallback(async (column: RosterSortColumn) => {
    const direction = sort?.column === column && sort.direction === 'asc' ? 'desc' : 'asc';
    const next = sortRoster(participants, column, direction);
    setSort({ column, direction });
    setParticipants(next);
    setSaveStatus('saving');
    try {
      await saveRosterOrder(currentProgram!.id, next.map(p => p.id));
      // Marked stale, not refetched: a refetch would overwrite rows still saving.
      queryClient.invalidateQueries({ queryKey: ['roster', currentProgram!.id], refetchType: 'none' });
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
    }
  }, [sort, participants, currentProgram, queryClient]);

  const handleDelete = useCallback(async (id: string) => {
    setSaveStatus('saving');
    setParticipants(prev => prev.filter(p => p.id !== id));
    try {
      await apiDeleteParticipant(currentProgram!.id, id);
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
    }
  }, [currentProgram]);

  const handleAdd = useCallback(async (data: Omit<RosterParticipant, 'id'>) => {
    const newId = uuidv4();
    const newParticipant: RosterParticipant = { id: newId, ...data };
    setSaveStatus('saving');
    setParticipants(prev => [...prev, newParticipant]);
    try {
      await upsertParticipant(currentProgram!.id, newId, data);
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
    }
  }, [currentProgram]);

  /** Dev-only convenience: bulk-adds fake participants so the roster can be
   * exercised locally without hand-typing rows. Never rendered outside
   * `npm start` (NODE_ENV === 'development'). */
  const handleSeedTestData = useCallback(async () => {
    const raw = window.prompt('How many test participants to add?', '20');
    if (raw === null) return;
    const count = parseInt(raw, 10);
    if (!Number.isFinite(count) || count <= 0) return;

    setSaveStatus('saving');
    try {
      const existingNames = new Set(participants.map(p => p.name));
      for (const data of generateTestParticipants(count, existingNames)) {
        const newId = uuidv4();
        setParticipants(prev => [...prev, { id: newId, ...data }]);
        await upsertParticipant(currentProgram!.id, newId, data);
      }
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
    }
  }, [currentProgram, participants]);

  const handleKeepTogetherToggle = useCallback(async (id: string) => {
    const participant = participants.find(p => p.id === id);
    if (!participant?.partner_id) return;

    const newValue = !participant.keep_together;
    const partnerId = participant.partner_id;

    // Optimistic update on both sides
    setParticipants(prev => prev.map(p => {
      if (p.id === id || p.id === partnerId) {
        return { ...p, keep_together: newValue };
      }
      return p;
    }));

    setSaveStatus('saving');
    try {
      const partnerObj = participants.find(p => p.id === partnerId);
      await upsertParticipant(currentProgram!.id, id, { ...participant, keep_together: newValue });
      if (partnerObj) {
        await upsertParticipant(currentProgram!.id, partnerId, { ...partnerObj, keep_together: newValue });
      }
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
    }
  }, [participants, currentProgram]);

  // Only this key. The rules live on the program document: adding or removing
  // one writes no roster document, and /api/roster/canonical serves the frozen
  // participant_data, which only a rebuild rewrites. Refetching the roster here
  // would be a chance to overwrite the grid with a stale body for no gain.
  const invalidateKeepApart = () => {
    queryClient.invalidateQueries({ queryKey: ['keep-apart', currentProgram!.id] });
  };

  /** Deliberately not caught: the section renders the server's refusal inline,
   * beside the two selects that caused it, rather than at the top of the page. */
  const handleAddKeepApart = async (aId: string, bId: string) => {
    await addKeepApart(currentProgram!.id, aId, bId);
    invalidateKeepApart();
  };

  /** Removal is never refused, so the only realistic failure is the network.
   * The section's contract is that this must not reject, so it is reported
   * through the page's own error alert. */
  const handleRemoveKeepApart = async (aId: string, bId: string) => {
    try {
      await removeKeepApart(currentProgram!.id, aId, bId);
      invalidateKeepApart();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const invalidateAssignmentQueries = () => {
    const programId = currentProgram?.id;
    queryClient.invalidateQueries({ queryKey: ['assignment-set-metadata', programId] });
    queryClient.invalidateQueries({ queryKey: ['versions', programId] });
    queryClient.invalidateQueries({ queryKey: ['results', programId] });
  };

  /**
   * One request. The server refuses, solves, and repoints the program itself,
   * so there is nothing left for the page to do but wait and report back.
   */
  const handleRebuild = async () => {
    setError(null);
    setNotice(null);
    setGenerating(true);
    try {
      const result = await generateFromRoster(
        currentProgram!.id, parseInt(numTables), parseInt(numSessions)
      );
      invalidateAssignmentQueries();
      queryClient.invalidateQueries({ queryKey: ['canonical-roster', currentProgram!.id] });
      if (result.rebuilt) {
        navigate(`/table-assignments?program=${currentProgram!.id}`);
        return;
      }
      // Nothing about the sessions moved, so sending the coordinator to look
      // at them would be a non-sequitur.
      setNotice(result.message);
      // The change was accepted, so the guard goes back on - the page stays put
      // and would otherwise leave the grid editable after a successful save.
      setArmed(false);
      setGenerating(false);
    } catch (err: any) {
      // The server's wording is the user-facing wording; don't rewrite it.
      setError(err.message);
      setGenerating(false);
    }
  };

  const handleDiscard = async () => {
    const count = changeset.total;
    const parts = [
      count > 0 && `${count} roster change${count === 1 ? '' : 's'}`,
      drafts.length > 0 &&
        `${drafts.length} unsaved ${drafts.length === 1 ? 'person' : 'people'} from your upload`,
    ].filter(Boolean);
    if (!window.confirm(`Discard ${parts.join(' and ')}?`)) return;
    setError(null);
    setNotice(null);
    try {
      await discardRosterChanges(currentProgram!.id);
      setArmed(false);
      // The selects are React state, and discard does not change the set id, so
      // the effect that syncs them will not re-run. Without this a discarded
      // session-count change stays on screen and the page goes on reporting an
      // unsaved change that Discard can no longer clear.
      if (canonical?.num_tables) setNumTables(String(canonical.num_tables));
      if (canonical?.num_sessions) setNumSessions(String(canonical.num_sessions));
      queryClient.invalidateQueries({ queryKey: ['roster', currentProgram!.id] });
      queryClient.invalidateQueries({ queryKey: ['canonical-roster', currentProgram!.id] });
      queryClient.invalidateQueries({ queryKey: ['roster-drafts', currentProgram!.id] });
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Errors propagate so the dialog shows them and stays open.
  const handleImport = async (data: RosterUpload) => {
    await uploadRoster(currentProgram!.id, data);
    setUpload(null);
    setSort({ column: 'name', direction: 'asc' });
    queryClient.invalidateQueries({ queryKey: ['roster', currentProgram!.id] });
    queryClient.invalidateQueries({ queryKey: ['roster-drafts', currentProgram!.id] });
    queryClient.invalidateQueries({ queryKey: ['keep-apart', currentProgram!.id] });
  };

  const handleDraftChange = async (id: string, patch: DraftPatch) => {
    setError(null);
    setSaveStatus('saving');
    try {
      const { participant } = await updateRosterDraft(currentProgram!.id, id, patch);
      setSaveStatus('saved');
      queryClient.invalidateQueries({ queryKey: ['roster-drafts', currentProgram!.id] });
      if (participant) queryClient.invalidateQueries({ queryKey: ['roster', currentProgram!.id] });
    } catch (err) {
      setSaveStatus('error');
      setError(err instanceof Error ? err.message : 'Could not save that change.');
    }
  };

  const handleDraftDelete = async (id: string) => {
    setError(null);
    try {
      await deleteRosterDraft(currentProgram!.id, id);
      queryClient.invalidateQueries({ queryKey: ['roster-drafts', currentProgram!.id] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove that person.');
    }
  };

  const changeset = computeChangeset(
    canonical?.participants ?? [],
    toCanonical(participants, keepApartPairs),
    { tables: canonical?.num_tables ?? null, sessions: canonical?.num_sessions ?? null },
    { tables: parseInt(numTables), sessions: parseInt(numSessions) },
    drafts.map(d => d.name),
  );
  // The lock is derived, never stored: a current set exists, the roster still
  // matches the one it was built from, and the coordinator hasn't asked to edit.
  // A draft can only be fixed in an editable grid, so drafts keep the page unlocked.
  const locked = !!currentSet && !changeset.isDirty && !armed && drafts.length === 0;

  // Post-build the assignment set owns absences, so the Away column mirrors the
  // set's per-session absences (served on the canonical roster) whether locked
  // or not. The dormant roster-document field is allowed to drift, and showing
  // it once the roster was unlocked made a stale value look editable.
  const gridParticipants = (() => {
    if (!currentSet) return participants;
    const absentByName = new Map(
      (canonical?.participants ?? []).map(p => [p.name, p.absent_sessions ?? []]),
    );
    return participants.map(p => ({
      ...p,
      absent_sessions: absentByName.get(p.name) ?? [],
    }));
  })();

  // Matches check_shortfalls on the server: a table with one person is not a
  // discussion group, and a table with no facilitator can't run. Disagreeing
  // meant enabling the button and refusing the request a round trip later.
  const minParticipants = parseInt(numTables) * 2;
  const numFacilitators = participants.filter(p => p.is_facilitator).length;
  const facilitatorShortfall = numFacilitators < parseInt(numTables);
  const draftsBlock = drafts.length > 0;
  const draftCountText = `${drafts.length} ${drafts.length === 1 ? 'person' : 'people'}`;
  const canGenerate = !draftsBlock && participants.length >= minParticipants && !facilitatorShortfall;
  // computeChangeset reports a brand-new program as clean - there is no
  // canonical roster to differ from - so dirtiness alone would hide the button
  // on exactly the program that needs it.
  const hasAssignmentSet = !!currentSet;
  // Drafts too, so the disabled button and its reason stay visible.
  const showActions = changeset.isDirty || !hasAssignmentSet || draftsBlock;

  // Keep-apart rules as name pairs, for the upload dialog's warning. A pair can
  // name a draft, which keeps its id when it saves.
  const draftNames = new Map(drafts.map(d => [d.id, d.name]));
  const nameById = new Map<string, string>([
    ...participants.map(p => [p.id, p.name] as [string, string]),
    ...Array.from(draftNames),
  ]);
  const keepApartNames = keepApartPairs.flatMap(([a, b]) => {
    const aName = nameById.get(a);
    const bName = nameById.get(b);
    return aName && bName ? [[aName, bName] as [string, string]] : [];
  });

  if (loading) {
    return (
      <div
        className="container mx-auto p-4 flex justify-center"
        role="status"
        aria-label="Loading roster"
      >
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <Card
        className={`w-full max-w-4xl mx-auto ${generating ? 'pointer-events-none opacity-60' : ''}`}
        aria-busy={generating}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Roster</CardTitle>
              <CardDescription>
                {locked
                  ? 'Locked \u2014 these are the people your sessions were built from'
                  : changeset.total > 0
                    ? `${changeset.total} change${changeset.total === 1 ? '' : 's'} not yet in your sessions`
                    : 'Manage your participants'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                {saveStatus === 'saving' && 'Saving...'}
                {saveStatus === 'saved' && 'Saved'}
                {saveStatus === 'error' && 'Save failed'}
              </span>
              {locked && (
                <Button variant="outline" size="sm" onClick={() => setArmed(true)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit roster
                </Button>
              )}
              {!locked && (
                <RosterUploadButton
                  onLoaded={roster => {
                    setError(null);
                    setUpload(roster);
                  }}
                  onError={setError}
                />
              )}
            </div>
          </div>
        </CardHeader>
        {process.env.NODE_ENV === 'development' && !locked && navActionsSlot &&
          createPortal(
            <Button variant="outline" size="sm" onClick={handleSeedTestData}>
              Add test data
            </Button>,
            navActionsSlot,
          )}
        <CardContent className="space-y-6">
          {draftsBlock && (
            <Alert variant="destructive" role="status">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>
                  {`${draftCountText} from your upload ${drafts.length === 1 ? "isn't" : "aren't"} saved yet.`}
                </strong>{' '}
                Fix the highlighted cells and each person saves on their own.
              </AlertDescription>
            </Alert>
          )}
          <PopulationStats participants={participants} keepApartPairs={keepApartPairs} />

          <RosterGrid
            participants={gridParticipants}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onAdd={handleAdd}
            onKeepTogetherToggle={handleKeepTogetherToggle}
            numSessions={parseInt(numSessions)}
            readOnly={locked}
            drafts={drafts}
            onDraftChange={handleDraftChange}
            onDraftDelete={handleDraftDelete}
            sort={sort}
            onSort={handleSort}
            absencesReadOnly={!!currentSet}
          />

          {changeset.isDirty && <ChangesetPanel changeset={changeset} />}

          <div className="flex space-x-4">
            <div className="flex-1">
              <Label htmlFor="num-tables">Number of Tables</Label>
              <Select value={numTables} disabled={locked} onValueChange={setNumTables}>
                <SelectTrigger id="num-tables">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: MAX_TABLES }, (_, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label htmlFor="num-sessions">Number of Sessions</Label>
              <Select value={numSessions} disabled={locked} onValueChange={setNumSessions}>
                <SelectTrigger id="num-sessions">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: MAX_SESSIONS }, (_, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <KeepApartSection
            participants={participants}
            pairs={keepApartPairs}
            onAdd={handleAddKeepApart}
            onRemove={handleRemoveKeepApart}
            readOnly={locked}
            pendingNames={draftNames}
          />

          {(error || fetchError || canonicalError || keepApartError || draftsError) && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              {/* A failure to load is not a refusal to rebuild, and a canonical
                  failure is neither - without it the page renders a confident
                  Locked and silently offers no way to save what gets typed. */}
              <AlertTitle>
                {error
                  ? 'Can’t rebuild yet'
                  : canonicalError
                    ? 'Can’t tell whether your roster matches your sessions'
                    : keepApartError
                      // Named, not implied: an unread rule renders as "nobody is
                      // being kept apart yet", which is a claim rather than an
                      // absence - and the roster reads dirty beside it.
                      ? 'Couldn’t load who is being kept apart'
                      : draftsError
                        // Same reason: unread drafts would look like none, and
                        // the build button would offer to leave them out.
                        ? 'Couldn’t load the people from your upload'
                        : 'Couldn’t load your roster'}
              </AlertTitle>
              <AlertDescription>
                {error ||
                  (fetchError as Error)?.message ||
                  'Reload the page before making changes — edits made now may not be saved to your sessions.'}
              </AlertDescription>
            </Alert>
          )}

          {notice && (
            <Alert>
              <AlertTitle>Saved</AlertTitle>
              <AlertDescription>{notice}</AlertDescription>
            </Alert>
          )}

          {generating ? (
            <div className="flex flex-col items-center justify-center gap-3 rounded-md border p-8 text-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {hasAssignmentSet ? 'Rebuilding' : 'Building'} your sessions… this can take up to two minutes.
              </p>
            </div>
          ) : showActions && (
            <div className="flex gap-3 items-center">
              <div className="flex-1 flex items-center gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleRebuild}
                  disabled={!canGenerate}
                >
                  {hasAssignmentSet ? 'Save and rebuild sessions' : 'Generate assignments'}
                </Button>
                {!canGenerate && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground"
                          aria-label="Why is this disabled?"
                        >
                          <HelpCircle className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {draftsBlock
                          ? `${draftCountText} from your upload ${drafts.length === 1 ? "isn't" : "aren't"} on the roster yet. Fix the highlighted cells first.`
                          : participants.length < minParticipants
                          ? `Need at least ${minParticipants} participants for ${numTables} tables — you have ${participants.length}.`
                          : `Need at least ${numTables} facilitators for ${numTables} tables — you have ${numFacilitators}.`}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              {hasAssignmentSet && (
                <Button variant="outline" onClick={handleDiscard}>
                  Discard changes
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      {upload && (
        <RosterImportDialog
          headers={upload.headers}
          rows={upload.rows}
          currentCount={participants.length + drafts.length}
          keepApartNames={keepApartNames}
          onCancel={() => setUpload(null)}
          onCommit={handleImport}
        />
      )}
    </div>
  );
}
